import { useCallback, useEffect, useState } from 'react';

import { AuthApi, errorKey, type LaunchTicket, type Session } from './auth/api';
import { AuthSheet } from './auth/AuthSheet';
import { loadSession, saveSession } from './auth/store';
import { config } from './config';
import { GameApi } from './profile/api.ts';
import { AppShell } from './layout/AppShell';
import { GameBox } from './game/GameBox';
import { Toast } from './ui/Toast';
import { usePaymentWatcher } from './shop/usePaymentWatcher';
import { SettingsSheet } from './SettingsSheet';
import { ActivityTab } from './tabs/ActivityTab';
import { AgentTab } from './tabs/AgentTab';
import { DepositTab } from './tabs/DepositTab';
import { LobbyTab } from './tabs/LobbyTab';
import { ProfileTab } from './tabs/ProfileTab';
import { useI18n } from './i18n/I18nProvider';
import { useRouter } from './useRouter';

const api = new AuthApi(config.accountUrl);
const game = new GameApi(config.gatewayUrl);

export function App() {
  const { t, i18n } = useI18n();
  const { tab, go } = useRouter();
  const [session, setSession] = useState<Session | null>(
    () => loadSession(typeof localStorage === 'undefined' ? null : localStorage));
  const [sheet, setSheet] = useState<'auth' | 'settings' | null>(null);
  //: 正在本页里嵌开的游戏地址。null = 没开。
  const [play, setPlay] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'warn' } | null>(null);
  //: 顶栏右上角的昵称。登录后拉一次资料；改名时由个人页回写
  const [nickname, setNickname] = useState<string | null>(null);

  useEffect(() => {
    setNickname(null);
    if (!session) return;
    let alive = true;
    api.profile(session.token)
      .then((p) => { if (alive) setNickname(p.nickname); })
      // 拉不到就先显示 uid 前几位顶着。**不弹错** —— 顶栏不是报错的地方
      .catch(() => { /* noop */ });
    return () => { alive = false; };
  }, [session]);

  // **盯着待付订单，挂在这一层**（不是收款面板里）—— 用户关掉面板、
  // 切到别的标签页、甚至进了游戏，那笔到账都得告诉他。
  // 不然他只会看到余额莫名其妙变了，或者更糟：以为没付成功，又付一次。
  const { watch } = usePaymentWatcher(api, session, useCallback((o) => {
    setToast(o.status === 'credited'
      ? { msg: t('pay.toastOk', { v: i18n.num(o.coins) }), kind: 'ok' }
      : { msg: t(`pay.st.${o.status}` as 'pay.st.expired'), kind: 'warn' });
  }, [t, i18n]));

  // 存下来的 token 可能已经被撤销/过期了。启动时验一次，**无声地**清掉坏的 ——
  // 不验的话，用户会看到"已登录"的界面，然后每个操作都失败。
  useEffect(() => {
    if (!session) return;
    let alive = true;
    api.whoami(session.token).catch(() => {
      if (alive) { setSession(null); saveSession(localStorage, null); }
    });
    return () => { alive = false; };
    // 只在启动时验一次；后面的失效由各次操作自己报
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAuthDone = useCallback((s: Session) => {
    setSession(s);
    saveSession(localStorage, s);
    setSheet(null);
  }, []);

  const onLogout = useCallback(() => {
    if (session) api.logout(session.token).catch(() => { /* 本地先登出就够了 */ });
    setSession(null);
    saveSession(localStorage, null);
    setSheet(null);
  }, [session]);

  const onPlay = useCallback(async (code: string) => {
    if (!session) { setSheet('auth'); return; }
    // 进游戏：**先换一张一次性的启动令牌，再把它拼进 URL**。
    //
    // 以前这里拼的是会话 token 本身。它有两个不同的毛病：URL 会进浏览器
    // 历史、会被 referrer 带走、页面上任何脚本都读得到 `location.search`
    // （而游戏页里跑着一整包来路不明的原版代码）；而且那是一张全权凭据 ——
    // 能绑 CPF、能申请提现、能改密码。
    //
    // 现在进 URL 的是启动令牌：60 秒、一次性（gateway 那边取走即焚）。
    // 游戏客户端拿它换一张 play 会话，account 一概不认那张票。
    // 全流程见 apps/account/src/account/launch.py。
    let ticket: LaunchTicket;
    try {
      ticket = await api.launch(session.token, code, i18n.locale);
    } catch (e) {
      // **换不到就别开那个页面。** 硬开的话玩家看到的是游戏里一句
      // 英文报错，而真正的原因（会话过期了 / 这个游戏在维护）说得清楚。
      setToast({ msg: t(errorKey(e)), kind: 'warn' });
      return;
    }
    // **后端由服务端选。** PG 回的是 account 上的相对地址（那一页取走令牌、
    // 去 PG 拿游戏 HTML）—— 按 account 的源拼，和别的 account 请求同一个源。
    if ('url' in ticket) {
      setPlay(new URL(ticket.url, config.accountUrl).toString());
      return;
    }
    const u = new URL(config.gameUrl);
    u.searchParams.set('gw', config.gatewayUrl);
    u.searchParams.set('lt', ticket.token);
    u.searchParams.set('game', code);
    // **把玩家选的语言也带过去。** 游戏里那包原版代码只有葡语，但被服务端
    // 拒掉的那些话（"超过单局限红"之类）是我们自己要显示的 ——
    // 不带的话，一个中文用户在游戏里会看到葡语，或者更糟：看到中文原文，
    // 而他本来选的是英语。
    u.searchParams.set('lang', i18n.locale);
    // **在本页里嵌开，不整页跳走、更不开新窗口**（见 ui/PageFrame.tsx）。
    // 整页跳走的话，大厅会被卸载掉 —— 回来要重新加载、重新校验会话，
    // 而压壳成 App 之后那一下更难看：白屏一秒，然后从头来过。
    setPlay(u.toString());
  }, [session, t, i18n]);

  // 稳定引用：PageFrame 拿它挂 popstate，每次渲染换一个会反复 push 历史记录
  const closeGame = useCallback(() => setPlay(null), []);

  return (
    <>
      <AppShell
        tab={tab}
        onGo={go}
        session={session}
        nickname={nickname}
        onAuth={() => setSheet(session ? 'settings' : 'auth')}
        onSettings={() => setSheet('settings')}
      >
        {tab === 'lobby' && <LobbyTab api={api} onPlay={(c) => void onPlay(c)} />}
        {tab === 'activity' && <ActivityTab />}
        {tab === 'agent' && (
          <AgentTab api={api} game={game} session={session}
                    onNeedLogin={() => setSheet('auth')} />
        )}
        {tab === 'deposit' && (
          <DepositTab api={api} game={game} session={session}
                      onWatch={watch}
                      onNeedLogin={() => setSheet('auth')} />)}
        {tab === 'profile' && (
          <ProfileTab api={api} game={game} session={session}
                      accountUrl={config.accountUrl}
                      onNickname={setNickname}
                      onNeedLogin={() => setSheet('auth')} />)}
      </AppShell>

      <AuthSheet open={sheet === 'auth'} api={api}
                 onClose={() => setSheet(null)} onDone={onAuthDone} />
      <SettingsSheet open={sheet === 'settings'} api={api} session={session}
                     onClose={() => setSheet(null)} onLogout={onLogout} />

      {/* 游戏也在本页里嵌开，外面套一层我们自己的壳（悬浮返回键等）。
          iframe 仍然是 PageFrame 开的 —— **整个大厅只有这一个开外部页面的口子。** */}
      <GameBox url={play} onExit={closeGame} />

      {/* 到账通知**盖在所有东西上面**，包括游戏那一层 */}
      {toast && <Toast message={toast.msg} kind={toast.kind}
                       onClose={() => setToast(null)} />}
    </>
  );
}
